import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Skull } from 'lucide-react';
import CommonModal from '../../ui/CommonModal';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { Avatar } from '../../ui/Avatar';
import { LoadingSpinner } from '../../ui/Loading';
import { User } from '../../../types';
import { formatCurrency } from '../../../utils';
import { useTerminatePlayer, useTerminatePlayerPreview } from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';

const TERMINATE_CONFIRM_WORD = 'terminate';

interface TerminatePlayerModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'preview' | 'confirm' | 'executing' | 'success' | 'error';

function playerPrimaryLine(u: User) {
  return u.phone?.trim() || u.email?.trim() || '—';
}

const TerminatePlayerModal: React.FC<TerminatePlayerModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>('preview');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [failureDetails, setFailureDetails] = useState<
    Array<{ providerCode?: string; message?: string }>
  >([]);

  const previewQuery = useTerminatePlayerPreview(user?.id || '', isOpen && !!user);
  const terminateMutation = useTerminatePlayer();

  const preview = previewQuery.data?.data;

  useEffect(() => {
    if (!isOpen) {
      setStep('preview');
      setReason('');
      setConfirmText('');
      setExecuteError(null);
      setFailureDetails([]);
    }
  }, [isOpen]);

  if (!isOpen || !user) return null;

  const confirmPhraseOk = confirmText.trim().toLowerCase() === TERMINATE_CONFIRM_WORD;
  const reasonOk = reason.trim().length > 0;
  const canSubmitConfirm =
    preview?.canTerminate === true && reasonOk && confirmPhraseOk && !terminateMutation.isLoading;

  const avatarUser = {
    id: user.id,
    name: user.name ?? undefined,
    userName: user.userName ?? undefined,
    username: user.username ?? undefined,
    email: user.email ?? undefined,
    profile: user.profile,
  };

  const handleClose = () => {
    if (step === 'executing' || terminateMutation.isLoading) return;
    onClose();
  };

  const handleContinue = () => {
    if (!preview?.canTerminate) return;
    setStep('confirm');
  };

  const handleSubmit = async () => {
    if (!canSubmitConfirm || !user.id) return;
    setStep('executing');
    setExecuteError(null);
    setFailureDetails([]);
    try {
      const res = await terminateMutation.mutateAsync({ userId: user.id, reason: reason.trim() });
      setStep('success');
      toast.success(res.message || 'Player terminated. Game balances recalled to main wallet.');
    } catch (err: unknown) {
      setStep('error');
      const ax = err as { response?: { data?: { message?: string; failures?: unknown[]; blockers?: unknown[] } } };
      const msg = ax.response?.data?.message || 'Termination failed. Account was not closed.';
      setExecuteError(msg);
      const failures = ax.response?.data?.failures;
      if (Array.isArray(failures)) {
        setFailureDetails(
          failures.map((f: { providerCode?: string; error?: string }) => ({
            providerCode: f.providerCode,
            message: f.error || msg,
          })),
        );
      }
      toast.error(msg);
    }
  };

  const gameWalletsLink = `/game-wallets?search=${encodeURIComponent(user.email || user.phone || user.id)}`;

  const title = (
    <span className="flex items-center gap-2 text-destructive">
      <Skull className="h-5 w-5 shrink-0" aria-hidden />
      Terminate player
    </span>
  );

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeDisabled={step === 'executing' || terminateMutation.isLoading}
      title={title}
    >
      <div className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
          <Avatar user={avatarUser} size="md" deterministicColor className="shrink-0" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate font-semibold">{playerPrimaryLine(user)}</p>
            <p className="font-mono text-[11px] text-muted-foreground truncate" title={user.id}>
              ID: {user.id}
            </p>
          </div>
        </div>

        <div
          className="rounded-lg border border-destructive/40 bg-red-50 p-3 text-sm text-red-950"
          role="alert"
        >
          <p className="font-semibold">Permanent account closure</p>
          <p className="mt-2">
            In-game balances will be moved to the player&apos;s main wallet. Main wallet balances are{' '}
            <strong>not</strong> withdrawn automatically. The player cannot deposit, withdraw, or play after
            termination.
          </p>
        </div>

        {step === 'preview' && previewQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : null}

        {step === 'preview' && previewQuery.isError ? (
          <p className="text-sm text-destructive">Unable to load termination preview.</p>
        ) : null}

        {preview && (step === 'preview' || step === 'confirm' || step === 'error') ? (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border bg-background p-3">
                <p className="text-muted-foreground">Est. in-game total</p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatCurrency(preview.totals.estimatedInGame)}
                </p>
              </div>
              <div className="rounded-md border bg-background p-3">
                <p className="text-muted-foreground">Main wallet</p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatCurrency(preview.totals.masterWalletBalance)}
                </p>
              </div>
            </div>

            {preview.providers.length > 0 ? (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Provider</th>
                      <th className="px-3 py-2 font-medium">Mode</th>
                      <th className="px-3 py-2 font-medium text-right">Est. in-game</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.providers.map((p) => (
                      <tr key={p.gameProviderId} className="border-t">
                        <td className="px-3 py-2">{p.providerName || p.providerCode}</td>
                        <td className="px-3 py-2 font-mono text-xs">{p.recallMode}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatCurrency(p.estimatedInGame)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No active game wallets for this player.</p>
            )}

            {preview.blockers.length > 0 ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="alert">
                <p className="font-semibold flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Termination blocked
                </p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {preview.blockers.map((b) => (
                    <li key={`${b.providerCode}-${b.recallMode}`}>
                      <span className="font-medium">{b.providerCode}</span>: {b.message}
                    </li>
                  ))}
                </ul>
                <p className="mt-2">
                  <Link to={gameWalletsLink} className="text-primary underline-offset-2 hover:underline">
                    Open game wallets
                  </Link>{' '}
                  to resolve manually.
                </p>
              </div>
            ) : null}
          </>
        ) : null}

        {step === 'confirm' || step === 'error' ? (
          <div className="space-y-3 border-t pt-4">
            <div className="space-y-2">
              <label htmlFor="terminate-reason" className="text-sm font-medium">
                Terminate reason <span className="text-red-500">*</span>
              </label>
              <Input
                id="terminate-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for terminating this player…"
                disabled={terminateMutation.isLoading}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="terminate-confirm" className="text-sm font-medium">
                Type <span className="font-mono">{TERMINATE_CONFIRM_WORD}</span> to confirm{' '}
                <span className="text-red-500">*</span>
              </label>
              <Input
                id="terminate-confirm"
                autoComplete="off"
                className="font-mono"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={TERMINATE_CONFIRM_WORD}
                disabled={terminateMutation.isLoading}
              />
            </div>
          </div>
        ) : null}

        {step === 'executing' ? (
          <div className="flex flex-col items-center gap-3 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin text-destructive" />
            <p>Recalling in-game balances and closing account…</p>
            <p className="text-xs">This may take up to a minute.</p>
          </div>
        ) : null}

        {step === 'success' && terminateMutation.data?.data?.gameRecall ? (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
            <p className="font-semibold">Termination complete</p>
            <p className="mt-1">
              Total recalled to main wallet:{' '}
              {formatCurrency(terminateMutation.data.data.gameRecall.totalRecalled)}
            </p>
          </div>
        ) : null}

        {step === 'error' && executeError ? (
          <div className="rounded-md border border-destructive/50 bg-red-50 p-3 text-sm text-red-900" role="alert">
            <p className="font-semibold">{executeError}</p>
            {failureDetails.length > 0 ? (
              <ul className="mt-2 list-disc pl-5">
                {failureDetails.map((f, i) => (
                  <li key={i}>
                    {f.providerCode ? `${f.providerCode}: ` : ''}
                    {f.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          {step === 'success' ? (
            <Button
              onClick={() => {
                onSuccess();
                onClose();
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={step === 'executing' || terminateMutation.isLoading}
              >
                Cancel
              </Button>
              {step === 'preview' ? (
                <Button
                  variant="destructive"
                  onClick={handleContinue}
                  disabled={!preview?.canTerminate || previewQuery.isLoading}
                >
                  Continue
                </Button>
              ) : null}
              {step === 'confirm' || step === 'error' ? (
                <Button
                  variant="destructive"
                  onClick={() => void handleSubmit()}
                  disabled={!canSubmitConfirm}
                >
                  Terminate player
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </CommonModal>
  );
};

export default TerminatePlayerModal;
